import React, { createContext, useCallback, useContext, useState } from "react";

import { RECOVERY_SESSION_KEY } from "@utils/constants/session-storage";
import { removeSessionStorage } from "@utils/storage/removeSessionStorage";
import setSessionStorageItem from "@utils/storage/setSessionStorageItem";

import {
  PasswordRecoveryContextValue,
  PasswordRecoveryProviderProps,
  PasswordRecoverySession,
  TruncatedInfo,
  TruncatedInfoContextValue,
} from "./PasswordRecoveryContext.types";

const IdentifierRecoveryContext =
  createContext<PasswordRecoveryContextValue | null>(null);
const TruncatedInfoContext = createContext<TruncatedInfoContextValue | null>(
  null
);

export const PasswordRecoveryProvider = ({
  children,
}: PasswordRecoveryProviderProps) => {
  const [identifier, setIdentifierState] = useState("");
  const [recoverySession, setRecoverySessionState] =
    useState<PasswordRecoverySession | null>(null);
  const [truncatedInfo, setTruncatedInfo] = useState<TruncatedInfo | null>(
    null
  );

  const setIdentifier = useCallback((value: string) => {
    setIdentifierState((previousIdentifier) => {
      if (previousIdentifier !== value) {
        setTruncatedInfo(null);
        setRecoverySessionState(null);
      }

      return value;
    });
  }, []);

  const setRecoverySession = useCallback(
    (session: PasswordRecoverySession | null) => {
      setRecoverySessionState(session);
      try {
        if (session) {
          setSessionStorageItem(RECOVERY_SESSION_KEY, {
            ...session,
            expiresAt: Date.now() + 10 * 60 * 1000,
          });
        } else {
          removeSessionStorage(RECOVERY_SESSION_KEY);
        }
      } catch {
        // ignore storage errors
      }
    },
    []
  );

  return (
    <IdentifierRecoveryContext.Provider
      value={{ identifier, recoverySession, setIdentifier, setRecoverySession }}
    >
      <TruncatedInfoContext.Provider
        value={{ setTruncatedInfo, truncatedInfo }}
      >
        {children}
      </TruncatedInfoContext.Provider>
    </IdentifierRecoveryContext.Provider>
  );
};

export const useIdentifierRecoveryContext =
  (): PasswordRecoveryContextValue => {
    const context = useContext(IdentifierRecoveryContext);
    if (!context) {
      throw new Error(
        "useIdentifierRecoveryContext must be used within a PasswordRecoveryProvider"
      );
    }
    return context;
  };

export const useTruncatedInfoContext = (): TruncatedInfoContextValue => {
  const context = useContext(TruncatedInfoContext);
  if (!context) {
    throw new Error(
      "useTruncatedInfoContext must be used within a PasswordRecoveryProvider"
    );
  }
  return context;
};
